import { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Autocomplete,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  LinearProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  IconFolder,
  IconFolderPlus,
  IconUpload,
  IconTrash,
  IconArrowLeft,
  IconX,
  IconCrop,
  IconCheck,
  IconAlertCircle,
} from "@tabler/icons-react";
import Cropper from "react-easy-crop";
import Button from "../components/Button";
import UserContext from "../contexts/UserContext";
import { fetchGet, fetchPost, fetchDelete, handleUpload } from "../util/helpers";

function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob returned null"))),
        "image/jpeg",
        0.95,
      );
    };
    image.onerror = () => reject(new Error("Failed to load image for cropping"));
    image.src = imageSrc;
  });
}

const sectionCardSx = {
  p: 0,
  borderRadius: 3,
  mb: 3,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  border: "1px solid rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionHeaderSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  px: 3,
  py: 2.5,
  borderBottom: "1px solid rgba(0,0,0,0.06)",
};

const dialogPaperSx = {
  borderRadius: 3,
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
};

export default function MediaAssetsPage() {
  const { user } = useContext(UserContext);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentFolderName, setCurrentFolderName] = useState("");
  const [files, setFiles] = useState([]);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadTags, setUploadTags] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cropping, setCropping] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const fileInputRef = useRef(null);

  const loadFolders = () => {
    fetchGet("/media/folders").then((res) => { if (res?.result) setFolders(res.result); }).catch(() => {});
  };

  const loadFiles = (folderId) => {
    fetchGet(`/media/folders/${folderId}/files`).then((res) => { if (res?.result) setFiles(res.result); }).catch(() => {});
  };

  useEffect(() => { loadFolders(); }, []);

  useEffect(() => {
    if (currentFolder) loadFiles(currentFolder);
  }, [currentFolder]);

  const handleCreateFolder = async () => {
    if (!folderName.trim()) return;
    try {
      await fetchPost("/media/folders", { name: folderName });
      setCreateFolderOpen(false);
      setFolderName("");
      loadFolders();
    } catch (e) {
      console.error("Error creating folder:", e);
    }
  };

  const handleDeleteFolder = async (id) => {
    try {
      await fetchDelete(`/media/folders/${id}`);
      if (currentFolder === id) {
        setCurrentFolder(null);
        setCurrentFolderName("");
      }
      loadFolders();
    } catch (e) {
      console.error("Error deleting folder:", e);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
    setUploadOpen(true);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSaveUpload = async () => {
    if (!uploadFile) {
      setSnackbar({ open: true, message: "No file selected", severity: "error" });
      return;
    }
    if (!currentFolder) {
      setSnackbar({ open: true, message: "No folder selected", severity: "error" });
      return;
    }
    setUploading(true);
    try {
      let fileToUpload = uploadFile;
      if (cropping && croppedAreaPixels && uploadPreview) {
        const croppedBlob = await getCroppedImg(uploadPreview, croppedAreaPixels);
        fileToUpload = new File([croppedBlob], uploadFile.name, { type: "image/jpeg" });
      }
      const fileId = `${Date.now()}_${fileToUpload.name}`;
      const url = await handleUpload(fileToUpload, "media_assets", fileId);
      if (!url) {
        throw new Error("Upload returned no URL");
      }
      await fetchPost(`/media/folders/${currentFolder}/files`, {
        url,
        fileName: fileToUpload.name,
        fileType: fileToUpload.type,
        tags: uploadTags,
      });
      setUploadOpen(false);
      resetUploadState();
      loadFiles(currentFolder);
      setSnackbar({ open: true, message: "File uploaded successfully!", severity: "success" });
    } catch (e) {
      console.error("Upload error:", e);
      const msg = e?.message || e?.statusText || "Upload failed. Check console for details.";
      setSnackbar({ open: true, message: `Upload failed: ${msg}`, severity: "error" });
    }
    setUploading(false);
  };

  const resetUploadState = () => {
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTags([]);
    setCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await fetchDelete(`/media/files/${fileId}`);
      loadFiles(currentFolder);
    } catch (e) {
      console.error("Error deleting file:", e);
    }
  };

  const openFolder = (folder) => {
    setCurrentFolder(folder.id);
    setCurrentFolderName(folder.name);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh", bgcolor: "#FAFAFA" }}>
      {/* Gradient Header Banner */}
      <Paper
        sx={{
          background: "linear-gradient(135deg, #7C3AED 0%, #9F25F6 40%, #C334EB 100%)",
          borderRadius: 3,
          px: 4,
          py: 4,
          mb: 4,
          boxShadow: "0 4px 20px rgba(159, 37, 246, 0.25)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            right: 120,
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
          }}
        />
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 0.5, position: "relative", zIndex: 1 }}>
          Media & Assets
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 15, position: "relative", zIndex: 1 }}>
          Organize, upload, and manage your creative assets
        </Typography>
      </Paper>

      {!currentFolder ? (
        <>
          {/* Folder List View */}
          <Paper sx={sectionCardSx}>
            <Box sx={sectionHeaderSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 4, height: 28, borderRadius: 2, background: "linear-gradient(180deg, #7C3AED, #C334EB)" }} />
                <IconFolder size={20} color="#9F25F6" />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 17 }}>Folders</Typography>
              </Box>
              <Button style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13 }} gradient onClick={() => setCreateFolderOpen(true)}>
                <IconFolderPlus size={16} style={{ marginRight: 6 }} /> Create Folder
              </Button>
            </Box>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2.5}>
                {folders.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: "center", py: 8 }}>
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <IconFolder size={36} stroke={1.5} color="#9F25F6" />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 16, color: "#374151", mb: 0.5 }}>No folders yet</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                        Create your first folder to start organizing your media assets.
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {folders.map((f) => (
                  <Grid item xs={6} sm={4} md={3} key={f.id}>
                    <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        cursor: "pointer",
                        textAlign: "center",
                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        position: "relative",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(159, 37, 246, 0.15)",
                          borderColor: "rgba(159, 37, 246, 0.3)",
                        },
                      }}
                      onClick={() => openFolder(f)}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          color: "#D1D5DB",
                          width: 28,
                          height: 28,
                          transition: "all 0.2s",
                          "&:hover": { color: "#EF4444", bgcolor: "rgba(239,68,68,0.06)" },
                        }}
                        onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f.id); }}
                      >
                        <IconTrash size={14} />
                      </IconButton>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #7C3AED, #C334EB)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 1.5,
                          boxShadow: "0 4px 12px rgba(159, 37, 246, 0.25)",
                        }}
                      >
                        <IconFolder size={26} stroke={1.5} color="#fff" />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }} noWrap>{f.name}</Typography>
                      {f.fileCount !== undefined && (
                        <Chip
                          label={`${f.fileCount} file${f.fileCount !== 1 ? "s" : ""}`}
                          size="small"
                          sx={{
                            bgcolor: "rgba(159, 37, 246, 0.06)",
                            color: "#7C3AED",
                            fontWeight: 500,
                            fontSize: 11,
                            height: 22,
                            mb: 0.5,
                          }}
                        />
                      )}
                      {f.createdAt && (
                        <Typography sx={{ fontSize: 12, color: "#9CA3AF", mt: 0.5 }}>
                          {(() => {
                            const ts = f.createdAt;
                            if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
                            if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString();
                            const d = new Date(ts);
                            return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
                          })()}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </>
      ) : (
        <>
          {/* Folder Detail View */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3,
              px: 1,
            }}
          >
            <IconButton
              onClick={() => { setCurrentFolder(null); setCurrentFolderName(""); }}
              sx={{
                bgcolor: "rgba(159, 37, 246, 0.06)",
                color: "#7C3AED",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: "rgba(159, 37, 246, 0.12)" },
              }}
            >
              <IconArrowLeft size={20} />
            </IconButton>
            <Breadcrumbs
              sx={{
                "& .MuiBreadcrumbs-separator": { color: "#D1D5DB" },
              }}
            >
              <MuiLink
                underline="hover"
                sx={{
                  cursor: "pointer",
                  color: "#7C3AED",
                  fontWeight: 500,
                  fontSize: 14,
                  "&:hover": { color: "#6D28D9" },
                }}
                onClick={() => { setCurrentFolder(null); setCurrentFolderName(""); }}
              >
                Media & Assets
              </MuiLink>
              <Typography sx={{ color: "#374151", fontWeight: 600, fontSize: 14 }}>{currentFolderName}</Typography>
            </Breadcrumbs>
          </Box>

          <Paper sx={sectionCardSx}>
            <Box sx={sectionHeaderSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 4, height: 28, borderRadius: 2, background: "linear-gradient(180deg, #7C3AED, #C334EB)" }} />
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #7C3AED, #C334EB)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconFolder size={18} color="#fff" />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 17 }}>{currentFolderName}</Typography>
              </Box>
              <Box>
                <input ref={fileInputRef} type="file" hidden accept="image/*,video/*" onChange={handleFileSelect} />
                <Button style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13 }} gradient onClick={() => fileInputRef.current?.click()}>
                  <IconUpload size={16} style={{ marginRight: 6 }} /> Upload Media
                </Button>
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>
              {files.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <IconUpload size={36} stroke={1.5} color="#9F25F6" />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 16, color: "#374151", mb: 0.5 }}>No media yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14, mb: 2 }}>
                    Upload your first file to start building your media library.
                  </Typography>
                  <Button style={{ borderRadius: 10, padding: "10px 24px", fontSize: 13 }} gradient onClick={() => fileInputRef.current?.click()}>
                    <IconUpload size={16} style={{ marginRight: 6 }} /> Upload Your First File
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  {files.map((file) => (
                    <Grid item xs={6} sm={4} md={3} key={file.id}>
                      <Paper
                        sx={{
                          borderRadius: 3,
                          overflow: "hidden",
                          position: "relative",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                          },
                          "&:hover .media-overlay": { opacity: 1 },
                          "&:hover .media-img": { transform: "scale(1.05)" },
                        }}
                      >
                        <Box sx={{ overflow: "hidden", position: "relative", height: 180 }}>
                          {file.fileType?.startsWith("video") ? (
                            <Box
                              component="video"
                              src={file.url}
                              className="media-img"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={file.url}
                              alt={file.fileName}
                              className="media-img"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                            />
                          )}
                          <Box
                            className="media-overlay"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.6) 100%)",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                              display: "flex",
                              alignItems: "flex-end",
                              p: 1.5,
                            }}
                          >
                            <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
                              {file.fileName}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              bgcolor: "rgba(255,255,255,0.9)",
                              backdropFilter: "blur(4px)",
                              width: 30,
                              height: 30,
                              transition: "all 0.2s",
                              "&:hover": { bgcolor: "#FEE2E2", color: "#EF4444" },
                            }}
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <IconTrash size={14} />
                          </IconButton>
                        </Box>
                        <Box sx={{ p: 1.5, borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#374151" }} noWrap>{file.fileName}</Typography>
                          {file.tags?.length > 0 && (
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.75 }}>
                              {file.tags.map((t) => (
                                <Chip
                                  key={t}
                                  label={t}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    height: 20,
                                    bgcolor: "rgba(159, 37, 246, 0.06)",
                                    color: "#7C3AED",
                                    fontWeight: 500,
                                    border: "1px solid rgba(159, 37, 246, 0.12)",
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #7C3AED, #C334EB)" }} />
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconFolderPlus size={20} color="#9F25F6" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Create Folder</Typography>
          </Box>
          <IconButton onClick={() => setCreateFolderOpen(false)} size="small" sx={{ color: "#9CA3AF" }}><IconX size={18} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Folder Name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            sx={{ mt: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} gradient onClick={handleCreateFolder}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Media Dialog */}
      <Dialog open={uploadOpen} onClose={() => { setUploadOpen(false); resetUploadState(); }} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #7C3AED, #C334EB)" }} />
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconUpload size={20} color="#9F25F6" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Upload Media</Typography>
          </Box>
          <IconButton onClick={() => { setUploadOpen(false); resetUploadState(); }} size="small" sx={{ color: "#9CA3AF" }}><IconX size={18} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            {uploadPreview && !cropping && (
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.06)",
                    display: "inline-block",
                  }}
                >
                  <Box
                    component="img"
                    src={uploadPreview}
                    alt="Preview"
                    sx={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", display: "block" }}
                  />
                </Box>
                <Typography sx={{ mt: 1, fontSize: 12, color: "#6B7280" }}>
                  {uploadFile?.name} ({(uploadFile?.size / 1024).toFixed(0)} KB)
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    style={{
                      borderRadius: 8,
                      padding: "6px 16px",
                      fontSize: 12,
                      background: "rgba(159, 37, 246, 0.06)",
                      color: "#7C3AED",
                      border: "1px solid rgba(159, 37, 246, 0.2)",
                    }}
                    onClick={() => setCropping(true)}
                  >
                    <IconCrop size={14} style={{ marginRight: 6 }} /> Crop / Resize
                  </Button>
                </Box>
              </Box>
            )}
            {cropping && uploadPreview && (
              <Box sx={{ position: "relative", height: 300, bgcolor: "#111", borderRadius: 3, overflow: "hidden" }}>
                <Cropper
                  image={uploadPreview}
                  crop={crop}
                  zoom={zoom}
                  aspect={undefined}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </Box>
            )}
            {cropping && (
              <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                <Button
                  style={{ borderRadius: 8, padding: "6px 16px", fontSize: 12 }}
                  gradient
                  onClick={() => setCropping(false)}
                >
                  <IconCheck size={14} style={{ marginRight: 6 }} /> Done Cropping
                </Button>
              </Box>
            )}
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={uploadTags}
              onChange={(_, val) => setUploadTags(val)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={option}
                    sx={{
                      bgcolor: "rgba(159, 37, 246, 0.06)",
                      color: "#7C3AED",
                      border: "1px solid rgba(159, 37, 246, 0.12)",
                      fontWeight: 500,
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags..."
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              )}
            />
            {uploading && (
              <Box>
                <LinearProgress
                  sx={{
                    borderRadius: 2,
                    height: 6,
                    bgcolor: "rgba(159, 37, 246, 0.08)",
                    "& .MuiLinearProgress-bar": {
                      background: "linear-gradient(90deg, #7C3AED, #C334EB)",
                      borderRadius: 2,
                    },
                  }}
                />
                <Typography sx={{ mt: 1, fontSize: 12, color: "#7C3AED", textAlign: "center", fontWeight: 500 }}>
                  Uploading...
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} onClick={() => { setUploadOpen(false); resetUploadState(); }}>Cancel</Button>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} gradient onClick={handleSaveUpload} disabled={uploading || !uploadFile}>
            {uploading ? "Uploading..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3, fontWeight: 600, fontSize: 13 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
